import React from 'react';
import { Card, CardContent } from '../ui/card';
import { CheckCircle, XCircle, Shield, Eye, FileText, DollarSign, AlertTriangle, Zap } from 'lucide-react';

const WhyGoldcifSection = () => {
  const benefits = [
    {
      icon: Shield,
      title: "Legal, structured exports",
      color: "green"
    },
    {
      icon: Eye,
      title: "Transparent process",
      color: "green"
    },
    {
      icon: FileText,
      title: "Full paperwork & tax coverage",
      color: "green"
    },
    {
      icon: DollarSign,
      title: "Tax Payment",
      color: "green"
    }
  ];

  const avoids = [
    {
      icon: AlertTriangle,
      title: "Undocumented gold",
      color: "red"
    },
    {
      icon: Zap,
      title: "Risky logistics",
      color: "red"
    },
    {
      icon: Shield,
      title: "Untraceable buyers",
      color: "red"
    },
    {
      icon: XCircle,
      title: "Advance payments",
      color: "red"
    }
  ];

  return (
    <section id="why-goldcif" className="py-24 bg-[#020817]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative order-2 lg:order-1">
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-[#040F2D]/15 rounded-3xl"></div>
            <img
              src="https://images.unsplash.com/photo-1610375461246-83df859d849d?w=1400&q=80&auto=format&fit=crop"
              alt="Refined gold bars representing legal traceable export"
              className="relative z-10 w-full h-[400px] object-cover rounded-3xl shadow-2xl"
            />
          </div>
          <div className="order-1 lg:order-2">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">Why Work With GOLDCIF?</h2>
            <p className="text-xl text-white/80 leading-relaxed mb-4">
              Compare what we offer versus what we avoid.
            </p>
            <p className="text-base text-white/70 leading-relaxed">
              From raw gold to refined bullion, every step is documented, taxed, insured and traceable — built to satisfy OECD due diligence and LBMA-grade refinery standards.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* What We Offer */}
          <div>
            <div className="flex items-center mb-8">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">What We Offer</h3>
            </div>

            <div className="grid gap-3">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="border border-green-200 hover:border-green-300 transition-all bg-[#0B132B]/60 shadow-sm hover:shadow-md">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-4 w-4 text-green-600" />
                        </div>
                        <h4 className="text-sm font-semibold text-green-700">
                          {benefit.title}
                        </h4>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* What We Avoid */}
          <div>
            <div className="flex items-center mb-8">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-2xl font-bold text-white">What We Avoid</h3>
            </div>

            <div className="grid gap-3">
              {avoids.map((avoid, index) => {
                const IconComponent = avoid.icon;
                return (
                  <Card key={index} className="border border-red-200 hover:border-red-300 transition-all bg-[#0B132B]/60 shadow-sm hover:shadow-md">
                    <CardContent className="p-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <IconComponent className="h-4 w-4 text-red-600" />
                        </div>
                        <h4 className="text-sm font-semibold text-red-700">
                          {avoid.title}
                        </h4>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#040F2D]/10 via-[#D4AF37]/10 to-[#040F2D]/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">Our Commitment</h3>
            <p className="text-lg text-white/80 max-w-4xl mx-auto leading-relaxed">
              GOLDCIF is built on <strong className="text-[#D4AF37]">transparency</strong>, 
              <strong className="text-[#D4AF37]"> legality</strong>, and <strong className="text-[#D4AF37]">security</strong>. 
              We make legitimate gold trading accessible to all qualified artisanal miners.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyGoldcifSection;